/* eslint-disable object-curly-newline */
import {
    HistoryState,
    NavigationState,
    Route,
} from './types'
import {
    createAction,
    createAsyncThunk,
} from '@reduxjs/toolkit'
import {RootState} from '../store'
import {joinChannel} from 'api/action/joinChannel'
import ninchatClient from 'api/NinchatClient'
import router from './router'


type AsyncThunkConfig = {
    state: RootState
}

type ThunkAPI = Parameters<Parameters<typeof createAsyncThunk>[1]>[1]

const navigatingUndefinedChannel = async (route: Route, thunkAPI: ThunkAPI) => {
    if (router.isRoute<'Channel'>('Channel', route)) {
        if (!ninchatClient.connected) {
            return Promise.resolve()
        }

        const {channel_id} = route.params

        const state = thunkAPI.getState() as RootState

        if (!(channel_id in state.ninchat.user_channels)) {
            try {
                await thunkAPI.dispatch(joinChannel({channel_id}))
            } catch (error) {} // eslint-disable-line no-empty

            return Promise.reject(state.navigation.position)
        }
    }

    return Promise.resolve()
}

const actionSideEffects = (route: Route, thunkAPI: ThunkAPI) => Promise.all([
    navigatingUndefinedChannel(route, thunkAPI),
])

export const back = createAction('NAVIGATION/back')

export const forward = createAction('NAVIGATION/forward')

export const go = createAction<number>('NAVIGATION/go')

export const init = createAction('NAVIGATION/init', (route?: Route) => ({
    payload: {
        route: route ?? router.match(window.location.hash.substr(1)),
    },
}))

export const replace = createAsyncThunk<{route: Route}, Route, AsyncThunkConfig>(
    'NAVIGATION/replace',
    async ({name, params}: Route, thunkAPI) => {
        const url = router.stringify(name, params)
        const route = {
            name,
            params,
        }

        try {
            await actionSideEffects(route, thunkAPI)
        } catch (value) {
            return thunkAPI.rejectWithValue(value)
        }

        const state: HistoryState = {
            position: thunkAPI.getState().navigation.position,
            route,
        }

        history.replaceState(state, '', `#${url}`)

        return {route}
    },
)

export const freeze = createAction(
    'NAVIGATION/freeze',
    (navigation: NavigationState) => {
        const {routes, position} = navigation
        const route = routes[position]
        if (!route) {
            return {payload: null}
        }

        const url = router.stringify(route.name, route.params)
        const state: HistoryState = {
            position,
            route,
        }
        history.pushState(state, '', `#${url}`)
        return {payload: null}
    },
)

export const push = createAsyncThunk<{route: Route}, Route, AsyncThunkConfig>(
    'NAVIGATION/push',
    async ({name, params}: Route, thunkAPI) => {
        const url = router.stringify(name, params)
        const route = {
            name,
            params,
        }
        try {
            await actionSideEffects(route, thunkAPI)
        } catch (value) {
            return thunkAPI.rejectWithValue(undefined)
        }

        const state: HistoryState = {
            position: thunkAPI.getState().navigation.position + 1,
            route,
        }

        if (history.state === null && history.length > 1) {
            history.replaceState(state, '', `#${url}`)
        } else {
            history.pushState(state, '', `#${url}`)
        }

        return {route}
    },
    {
        condition: ({name, params}, {getState}) => {
            if (!name) {
                return false
            }

            const {
                position,
                routes,
            } = getState().navigation

            const route = routes[position]
            const current = route ? router.stringify(route.name, route.params) : null
            const next = router.stringify(name, params)

            return !(current === next)
        },
    },
)
